import {useAuth} from 'app/modules/auth'
import {Group, Role} from 'app/shared/Common/Enums'
import {HasPermissionInGroup, HasPermissionInRole} from 'app/shared/Common/Util'
import {FC, Suspense, lazy} from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'

const PrivateRoutes = () => {
  const {currentUser} = useAuth()

  // Note: Arrange your page by Name Ascending
  const DashboardPage = lazy(() => import('../pages/dashboard/Wrapper'))
  const MaintenancePage = lazy(() => import('../pages/maintenance/Wrapper'))
  const ProfilePage = lazy(() => import('../pages/profile/Wrapper'))
  const PledgePage = lazy(() => import('../pages/pledge/Wrapper'))
  const DirectoryPage = lazy(() => import('../pages/directory/Wrapper'))
  const ReportsPage = lazy(() => import('../pages/reports/Wrapper'))
  const SubmissionPage = lazy(() => import('../pages/submission/Wrapper'))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        {HasPermissionInRole(currentUser, [Role.EXT_Report_Full, Role.EXT_Report_Read]) &&
        HasPermissionInGroup(currentUser, [Group.Member_User_Administrator, Group.Member_Normal_User, Group.Member_Stakeholder]) ? (
          <Route path='auth/*' element={<Navigate to='/dashboard' state={{isFromLogin: true}} />} />
        ) : (
          <Route path='auth/*' element={<Navigate to='/pledge' state={{isFromLogin: true}} />} />
        )}

        {/* Lazy Modules */}
        {HasPermissionInRole(currentUser, [Role.EXT_Report_Full, Role.EXT_Report_Read]) &&
          HasPermissionInGroup(currentUser, [Group.Member_User_Administrator, Group.Member_Normal_User, Group.Member_Stakeholder]) && (
            <Route
              path='dashboard'
              element={
                <SuspensedView>
                  <DashboardPage />
                </SuspensedView>
              }
            />
          )}

        <Route
          path='profile'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />

        {HasPermissionInRole(currentUser, [Role.EXT_Submission_Full, Role.EXT_Submission_Read]) && (
          <Route
            path='pledge/*'
            element={
              <SuspensedView>
                <PledgePage />
              </SuspensedView>
            }
          >
            <Route path=':pledgeId' element={<PledgePage />} />
          </Route>
        )}

        {HasPermissionInRole(currentUser, [Role.EXT_Submission_Full, Role.EXT_Submission_Read]) &&
          HasPermissionInGroup(currentUser, [Group.Member_User_Administrator, Group.Member_Normal_User, Group.Member_Stakeholder]) && (
            <Route
              path='program/*'
              element={
                <SuspensedView>
                  <SubmissionPage />
                </SuspensedView>
              }
            >
              <Route path=':programId' element={<SubmissionPage />} />
            </Route>
          )}

        {HasPermissionInRole(currentUser, [Role.EXT_Report_Full, Role.EXT_Report_Read]) && (
          <Route
            path='reports'
            element={
              <SuspensedView>
                <ReportsPage />
              </SuspensedView>
            }
          />
        )}

        <Route
          path='directory/*'
          element={
            <SuspensedView>
              <DirectoryPage />
            </SuspensedView>
          }
        >
          <Route path=':party' element={<DirectoryPage />} />
        </Route>

        {HasPermissionInRole(currentUser, [Role.EXT_Maintenance_Full, Role.EXT_Maintenance_Read, Role.EXT_User_Full, Role.EXT_User_Read]) && (
          <Route
            path='maintenance/*'
            element={
              <SuspensedView>
                <MaintenancePage />
              </SuspensedView>
            }
          />
        )}

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' replace />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--kt-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
